import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie } from 'lucide-react';

const KEY = 'proxy_cookie_consent';

/** Renvoie le choix de cookies stocké ('accepted' | 'refused' | null). Utile pour conditionner un futur outil de mesure d'audience. */
export function getCookieConsent(): 'accepted' | 'refused' | null {
  try {
    const v = localStorage.getItem(KEY);
    if (!v) return null;
    return (JSON.parse(v).choice as 'accepted' | 'refused') ?? null;
  } catch {
    return null;
  }
}

const CookieConsent: React.FC = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(KEY)) setVisible(true);
    } catch {
      /* localStorage indisponible : on n'affiche pas pour ne pas bloquer */
    }
  }, []);

  const decide = (choice: 'accepted' | 'refused') => {
    try {
      localStorage.setItem(KEY, JSON.stringify({ choice, at: new Date().toISOString() }));
      window.dispatchEvent(new CustomEvent('cookie-consent', { detail: choice }));
    } catch {
      /* ignore */
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Consentement aux cookies"
      className="fixed inset-x-0 bottom-[calc(env(safe-area-inset-bottom)+4.75rem)] md:bottom-0 z-[95] p-3 sm:p-4"
    >
      <div className="mx-auto max-w-3xl rounded-2xl border border-neutral-200 bg-white/95 p-4 shadow-[0_20px_50px_-20px_rgba(10,22,40,0.45)] backdrop-blur sm:p-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-600">
              <Cookie size={18} />
            </span>
            <p className="text-sm leading-snug text-neutral-600">
              Nous utilisons des cookies pour assurer le bon fonctionnement du site et, avec votre accord,
              en mesurer l'audience.{' '}
              <Link to="/cookies" className="font-semibold text-primary-600 underline-offset-2 hover:underline">
                En savoir plus
              </Link>
              .
            </p>
          </div>
          <div className="flex shrink-0 gap-2 sm:ml-auto">
            <button
              onClick={() => decide('refused')}
              className="flex-1 rounded-xl border border-neutral-300 px-4 py-2.5 text-sm font-bold text-neutral-700 transition-colors hover:bg-neutral-50 sm:flex-none"
            >
              Refuser
            </button>
            <button
              onClick={() => decide('accepted')}
              className="flex-1 rounded-xl bg-primary-600 px-4 py-2.5 text-sm font-bold text-white transition-colors hover:bg-primary-700 sm:flex-none"
            >
              Accepter
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
